import { useState, useCallback } from "react";
import { useSuccessSound } from "@/hooks/useSuccessSound";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { 
  DollarSign, 
  MapPin, 
  Clock, 
  Calendar, 
  Plane, 
  Car, 
  Plus, 
  Minus, 
  Copy, 
  Check,
  RotateCcw,
  Info,
  Sparkles,
  Mail,
  User,
  Phone,
  Share2
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { motion, AnimatePresence } from "framer-motion";
import { format } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import LuxuryQuotePreviewCard from "@/components/LuxuryQuotePreviewCard";
import SuccessSparkles from "@/components/SuccessSparkles";

// Import shared pricing constants - SINGLE SOURCE OF TRUTH
import {
  FLAT_RATE_DESTINATIONS,
  SMART_DESTINATIONS,
  BUSINESS_DIRECTORY,
  FORMULA_PRICING,
  LOCAL_SERVICE,
  normalizeSearchTerm,
  findBusiness,
  isLateNight as checkLateNight,
  checkFlatRate,
  type BusinessEntry,
} from "@/lib/pricing-constants";

interface QuoteResult {
  totalPrice: number;
  baseFare: number;
  mileageCost: number;
  timeCost: number;
  additionalStopsCost: number;
  lateNightSurcharge: number;
  gratuity: number;
  distance: number;
  duration: number;
  durationText: string;
  distanceText: string;
  pricingMethod: 'flat-rate' | 'local' | 'formula';
  flatRateDestination?: string;
  isFlat: boolean;
}

interface WeatherData {
  temperature: number;
  feelsLike: number;
  description: string;
  humidity: number;
  wind: string;
}

interface AIInsights {
  pricingAnalysis?: {
    explanation: string;
  };
  tripInsights?: {
    routeNotes: string;
    trafficEstimate?: string;
  };
  tips?: string[];
}

const FareCalculator = () => {
  const { toast } = useToast();
  const { playSuccessSound } = useSuccessSound();
  const [serviceType, setServiceType] = useState<'airport' | 'point-to-point'>('airport');
  const [pickupAddress, setPickupAddress] = useState("");
  const [dropoffAddress, setDropoffAddress] = useState("");
  const [pickupDate, setPickupDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [pickupTime, setPickupTime] = useState("12:00");
  const [additionalStops, setAdditionalStops] = useState(0);
  const [distanceOverride, setDistanceOverride] = useState("");
  const [durationOverride, setDurationOverride] = useState("");
  const [isCalculating, setIsCalculating] = useState(false);
  const [quoteResult, setQuoteResult] = useState<QuoteResult | null>(null);
  const [copied, setCopied] = useState(false);
  const [showManualInput, setShowManualInput] = useState(false);
  const [detectionMethod, setDetectionMethod] = useState<'smart' | 'google' | 'manual' | 'business' | null>(null);
  const [recognizedBusiness, setRecognizedBusiness] = useState<string | null>(null);
  const [showBookingDialog, setShowBookingDialog] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [customerName, setCustomerName] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [weather, setWeather] = useState<WeatherData | null>(null);
  const [aiInsights, setAIInsights] = useState<AIInsights | null>(null);
  const [showSuccessAnimation, setShowSuccessAnimation] = useState(false);

  // Phone number auto-formatting (XXX) XXX-XXXX
  const formatPhoneNumber = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    const limited = numbers.slice(0, 10);
    if (limited.length === 0) return '';
    if (limited.length <= 3) return `(${limited}`;
    if (limited.length <= 6) return `(${limited.slice(0, 3)}) ${limited.slice(3)}`;
    return `(${limited.slice(0, 3)}) ${limited.slice(3, 6)}-${limited.slice(6)}`;
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCustomerPhone(formatPhoneNumber(e.target.value));
  };
  const fetchWeather = useCallback(async () => {
    try {
      // Using Open-Meteo API for San Antonio area
      const url = `https://api.open-meteo.com/v1/forecast?latitude=29.4241&longitude=-98.4936&current=temperature_2m,apparent_temperature,weather_code,relative_humidity_2m,wind_speed_10m,wind_direction_10m&temperature_unit=fahrenheit&wind_speed_unit=mph&timezone=America%2FChicago`;
      const res = await fetch(url);
      const data = await res.json();
      
      const current = data.current;
      const windDir = getWindDirection(current.wind_direction_10m);
      
      setWeather({
        temperature: Math.round(current.temperature_2m),
        feelsLike: Math.round(current.apparent_temperature),
        description: getWeatherDescription(current.weather_code),
        humidity: current.relative_humidity_2m,
        wind: `${Math.round(current.wind_speed_10m)} mph ${windDir}`
      });
    } catch (err) {
      console.error('Weather fetch error:', err);
    }
  }, []);

  const getWindDirection = (degrees: number): string => {
    const directions = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
    const index = Math.round(degrees / 45) % 8;
    return directions[index];
  };

  const getWeatherDescription = (code: number): string => {
    const descriptions: { [key: number]: string } = {
      0: 'Clear', 1: 'Mainly Clear', 2: 'Partly Cloudy', 3: 'Overcast',
      45: 'Foggy', 48: 'Rime Fog', 51: 'Light Drizzle', 53: 'Drizzle', 55: 'Heavy Drizzle',
      61: 'Light Rain', 63: 'Rain', 65: 'Heavy Rain', 71: 'Light Snow', 73: 'Snow',
      75: 'Heavy Snow', 80: 'Rain Showers', 81: 'Rain Showers', 82: 'Heavy Showers',
      95: 'Thunderstorm', 96: 'Thunderstorm', 99: 'Severe Thunderstorm'
    };
    return descriptions[code] || 'Unknown';
  };

  // Generate AI insights based on trip data
  const generateAIInsights = useCallback((distance: number, duration: number, pricingMethod: string) => {
    const insights: AIInsights = {
      pricingAnalysis: {
        explanation: pricingMethod === 'flat-rate' 
          ? 'Flat rate destination - fixed pricing applies.'
          : pricingMethod === 'local'
            ? 'Local service rate applied for short-distance trip.'
            : `Distance exceeds 15-mile local threshold, using formula pricing.`
      },
      tripInsights: {
        routeNotes: distance > 30 
          ? 'Via major highway for fastest route'
          : 'Using optimal local routes',
        trafficEstimate: checkLateNight(pickupTime) 
          ? 'Light traffic expected at this time'
          : 'Normal traffic conditions expected'
      },
      tips: []
    };

    // Add context-aware tips
    if (pickupAddress.toLowerCase().includes('airport') || pickupAddress.toLowerCase().includes('sat')) {
      insights.tips?.push('Allow 10-15 minutes for airport pickup procedures');
      insights.tips?.push('Confirm your flight arrival time');
    }
    
    if (checkLateNight(pickupTime)) {
      insights.tips?.push('Late-Night/Early Morning Surcharge applied (9 PM - 5 AM)');
    }

    if (distance > 50) {
      insights.tips?.push('Consider a comfort break for longer journey');
    }

    setAIInsights(insights);
  }, [pickupTime, pickupAddress]);

  // Use imported isLateNight from shared pricing constants (aliased as checkLateNight)

  // Smart destination detection - now includes business directory
  const detectDestination = useCallback((pickup: string, dropoff: string): { distance: number; duration: number; detected: boolean; businessName?: string } => {
    // Check business directory first (Tier 0 - highest priority)
    const pickupBusiness = findBusiness(pickup);
    const dropoffBusiness = findBusiness(dropoff);
    
    if (pickupBusiness) {
      return { 
        distance: pickupBusiness.distance, 
        duration: pickupBusiness.duration, 
        detected: true,
        businessName: pickupBusiness.name
      };
    }
    
    if (dropoffBusiness) {
      return { 
        distance: dropoffBusiness.distance, 
        duration: dropoffBusiness.duration, 
        detected: true,
        businessName: dropoffBusiness.name
      };
    }
    
    // Fall back to keyword-based smart destinations (Tier 1)
    const destination = (pickup + ' ' + dropoff).toLowerCase();
    
    for (const dest of SMART_DESTINATIONS) {
      if (dest.keywords.some(kw => destination.includes(kw))) {
        return { distance: dest.distance, duration: dest.duration, detected: true };
      }
    }
    
    return { distance: 0, duration: 0, detected: false };
  }, []);

  // Use imported checkFlatRate from shared pricing constants
  const calculatePrice = useCallback((distance: number, duration: number): QuoteResult => {
    const lateNightFee = checkLateNight(pickupTime) ? FORMULA_PRICING.LATE_NIGHT_SURCHARGE : 0;
    const stopsCost = additionalStops * FORMULA_PRICING.ADDITIONAL_STOP;
    
    // Check for flat rate destination first
    const flatRate = checkFlatRate(dropoffAddress, distance) || checkFlatRate(pickupAddress, distance);
    
    if (flatRate) {
      // Flat rate pricing - gratuity already included in the flat rate
      const total = flatRate.rate + lateNightFee + stopsCost;
      
      return {
        totalPrice: total,
        baseFare: flatRate.rate,
        mileageCost: 0,
        timeCost: 0,
        additionalStopsCost: stopsCost,
        lateNightSurcharge: lateNightFee,
        gratuity: 0, // Already included in flat rate
        distance,
        duration,
        durationText: `${Math.round(duration)} mins`,
        distanceText: `${distance.toFixed(1)} miles`,
        pricingMethod: 'flat-rate',
        flatRateDestination: flatRate.name,
        isFlat: true,
      };
    }
    
    // Local flat rate (< LOCAL_SERVICE.DISTANCE_THRESHOLD miles)
    if (distance < LOCAL_SERVICE.DISTANCE_THRESHOLD) {
      const baseFare = LOCAL_SERVICE.BASE_FARE;
      const subtotal = baseFare + lateNightFee + stopsCost;
      const gratuity = subtotal * FORMULA_PRICING.GRATUITY_RATE;
      const total = subtotal + gratuity;
      
      return {
        totalPrice: total,
        baseFare,
        mileageCost: 0,
        timeCost: 0,
        additionalStopsCost: stopsCost,
        lateNightSurcharge: lateNightFee,
        gratuity,
        distance,
        duration,
        durationText: `${Math.round(duration)} mins`,
        distanceText: `${distance.toFixed(1)} miles`,
        pricingMethod: 'local',
        isFlat: false,
      };
    }
    
    // Formula pricing (≥ LOCAL_SERVICE.DISTANCE_THRESHOLD miles)
    const baseFare = FORMULA_PRICING.BASE_FEE;
    const mileageCost = distance * FORMULA_PRICING.PER_MILE;
    const timeCost = duration * FORMULA_PRICING.PER_MINUTE;
    const subtotal = baseFare + mileageCost + timeCost + lateNightFee + stopsCost;
    const gratuity = subtotal * FORMULA_PRICING.GRATUITY_RATE;
    const total = subtotal + gratuity;
    
    return {
      totalPrice: total,
      baseFare,
      mileageCost,
      timeCost,
      additionalStopsCost: stopsCost,
      lateNightSurcharge: lateNightFee,
      gratuity,
      distance,
      duration,
      durationText: `${Math.round(duration)} mins`,
      distanceText: `${distance.toFixed(1)} miles`,
      pricingMethod: 'formula',
      isFlat: false,
    };
  }, [pickupTime, additionalStops, dropoffAddress, pickupAddress]);

  const calculateQuote = async () => {
    if (!pickupAddress.trim() || !dropoffAddress.trim()) {
      toast({
        title: "Missing Information",
        description: "Please enter both pickup and drop-off addresses.",
        variant: "destructive",
      });
      return;
    }

    setIsCalculating(true);
    setQuoteResult(null);
    setDetectionMethod(null);
    setRecognizedBusiness(null);

    // Check for manual overrides first
    if (distanceOverride && durationOverride) {
      const distance = parseFloat(distanceOverride);
      const duration = parseFloat(durationOverride);
      
      if (!isNaN(distance) && !isNaN(duration)) {
        const result = calculatePrice(distance, duration);
        setQuoteResult(result);
        setDetectionMethod('manual');
        setIsCalculating(false);
        toast({
          title: "Quote Calculated",
          description: "Using manual distance and duration values.",
        });
        return;
      }
    }

    // Fetch weather data in parallel with quote calculation
    fetchWeather();

    // Call backend edge function which uses Google Maps Distance Matrix
    try {
      const { data, error } = await supabase.functions.invoke('calculate-quote', {
        body: {
          pickupAddress,
          dropoffAddress,
          pickupTime,
          extraStops: additionalStops,
        },
      });

      if (error) throw error;

      // Map edge function response to local QuoteResult format
      const distance = data.distance ?? 0;
      const duration = typeof data.time === 'string' 
        ? parseFloat(data.time) 
        : (data.time ?? 0);

      // Use the returned pricing or calculate locally for consistency
      const pricingMethod = data.isFlat ? 'flat-rate' : (distance < LOCAL_SERVICE.DISTANCE_THRESHOLD ? 'local' : 'formula');
      
      const result: QuoteResult = {
        totalPrice: data.total,
        baseFare: data.breakdown?.baseRate ?? data.breakdown?.baseFee ?? data.subtotal,
        mileageCost: data.breakdown?.mileageCharge ?? 0,
        timeCost: data.breakdown?.timeCharge ?? 0,
        additionalStopsCost: data.breakdown?.extraStops ?? 0,
        lateNightSurcharge: data.breakdown?.lateNightFee ?? 0,
        gratuity: data.gratuity ?? 0,
        distance,
        duration,
        durationText: typeof data.time === 'string' ? data.time : `${Math.round(duration)} mins`,
        distanceText: `${distance.toFixed(1)} miles`,
        pricingMethod,
        flatRateDestination: data.ruleApplied,
        isFlat: data.gratuityIncluded ?? data.isFlat ?? false,
      };

      setQuoteResult(result);
      
      // Generate AI insights based on the calculated data
      generateAIInsights(distance, duration, pricingMethod);
      
      // Determine detection method from response
      if (data.businessRecognized) {
        setDetectionMethod('business');
        setRecognizedBusiness(data.businessRecognized);
      } else {
        setDetectionMethod('google');
      }

      toast({
        title: "Quote Calculated",
        description: data.ruleApplied || "Distance calculated via Google Maps!",
      });
    } catch (err) {
      console.error("Error calculating quote:", err);
      
      // Fallback to local smart detection
      const smartDetection = detectDestination(pickupAddress, dropoffAddress);
      if (smartDetection.detected) {
        const result = calculatePrice(smartDetection.distance, smartDetection.duration);
        setQuoteResult(result);
        setDetectionMethod('smart');
        generateAIInsights(smartDetection.distance, smartDetection.duration, result.pricingMethod);
        if (smartDetection.businessName) {
          setRecognizedBusiness(smartDetection.businessName);
        }
        toast({
          title: "Quote Calculated",
          description: "Using local database (network error).",
        });
      } else {
        setShowManualInput(true);
        toast({
          title: "Enter Distance Manually",
          description: "Route not found. Please enter distance and duration below.",
          variant: "destructive",
        });
      }
    } finally {
      setIsCalculating(false);
    }
  };

  const resetForm = () => {
    setPickupAddress("");
    setDropoffAddress("");
    setPickupDate(format(new Date(), 'yyyy-MM-dd'));
    setPickupTime("12:00");
    setAdditionalStops(0);
    setDistanceOverride("");
    setDurationOverride("");
    setQuoteResult(null);
    setCopied(false);
    setShowManualInput(false);
    setDetectionMethod(null);
    setRecognizedBusiness(null);
  };

  const copyToClipboard = () => {
    if (!quoteResult) return;
    
    const text = `
Rick's Black Car Service - Quote
================================
Total Price: $${quoteResult.totalPrice.toFixed(2)}

Trip Details:
- Distance: ${quoteResult.distanceText}
- Duration: ${quoteResult.durationText}
- Pickup Date: ${pickupDate}
- Pickup Time: ${pickupTime}

Price Breakdown:
- ${quoteResult.pricingMethod === 'flat-rate' ? `Flat Rate (${quoteResult.flatRateDestination})` : quoteResult.pricingMethod === 'local' ? 'Local Base Fare' : 'Base Fare'}: $${quoteResult.baseFare.toFixed(2)}
${quoteResult.mileageCost > 0 ? `- Mileage Cost: $${quoteResult.mileageCost.toFixed(2)}` : ''}
${quoteResult.timeCost > 0 ? `- Time Cost: $${quoteResult.timeCost.toFixed(2)}` : ''}
${quoteResult.additionalStopsCost > 0 ? `- Additional Stops: $${quoteResult.additionalStopsCost.toFixed(2)}` : ''}
${quoteResult.lateNightSurcharge > 0 ? `- Late-Night/Early Morning Surcharge: $${quoteResult.lateNightSurcharge.toFixed(2)}` : ''}
${quoteResult.isFlat ? '- Gratuity: Included in flat rate' : `- Gratuity (20%): $${quoteResult.gratuity.toFixed(2)}`}

Book Now: 210-296-7409
    `.trim();
    
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    
    toast({
      title: "Copied!",
      description: "Quote details copied to clipboard.",
    });
  };

  const sendQuoteEmail = async () => {
    if (!quoteResult) return;
    
    if (!customerName.trim() || !customerEmail.trim() || !customerPhone.trim()) {
      toast({
        title: "Missing Information",
        description: "Please fill in all fields.",
        variant: "destructive",
      });
      return;
    }

    // Phone validation - must have 10 digits
    const phoneDigits = customerPhone.replace(/\D/g, '');
    if (phoneDigits.length !== 10) {
      toast({
        title: "Invalid Phone Number",
        description: "Please enter a valid 10-digit phone number.",
        variant: "destructive",
      });
      return;
    }

    setIsSendingEmail(true);

    try {
      const quoteDetails = `
Total Price: $${quoteResult.totalPrice.toFixed(2)}

Trip Details:
- Pickup: ${pickupAddress}
- Drop-off: ${dropoffAddress}
- Distance: ${quoteResult.distanceText}
- Duration: ${quoteResult.durationText}
- Date: ${pickupDate}
- Time: ${pickupTime}
- Additional Stops: ${additionalStops}

Price Breakdown:
- ${quoteResult.pricingMethod === 'flat-rate' ? `Flat Rate (${quoteResult.flatRateDestination})` : quoteResult.pricingMethod === 'local' ? 'Local Base Fare' : 'Base Fare'}: $${quoteResult.baseFare.toFixed(2)}
${quoteResult.mileageCost > 0 ? `- Mileage Cost: $${quoteResult.mileageCost.toFixed(2)}` : ''}
${quoteResult.timeCost > 0 ? `- Time Cost: $${quoteResult.timeCost.toFixed(2)}` : ''}
${quoteResult.additionalStopsCost > 0 ? `- Additional Stops: $${quoteResult.additionalStopsCost.toFixed(2)}` : ''}
${quoteResult.lateNightSurcharge > 0 ? `- Late-Night/Early Morning Surcharge: $${quoteResult.lateNightSurcharge.toFixed(2)}` : ''}
${quoteResult.isFlat ? '- Gratuity: Included in flat rate' : `- Gratuity (20%): $${quoteResult.gratuity.toFixed(2)}`}
      `.trim();

      const { error } = await supabase.functions.invoke('send-quote-email', {
        body: {
          name: customerName,
          email: customerEmail,
          phone: customerPhone,
          pickupDate: pickupDate,
          pickupTime: pickupTime,
          pickupLocation: pickupAddress,
          dropoffLocation: dropoffAddress,
          occasion: 'Fare Calculator Quote Request',
          passengers: '',
          message: `BOOKING REQUEST FROM FARE CALCULATOR\n\n${quoteDetails}`,
          pricingBreakdown: {
            total: quoteResult.totalPrice,
            distance: quoteResult.distance,
            duration: quoteResult.duration,
            baseFare: quoteResult.baseFare,
            mileageCost: quoteResult.mileageCost,
            timeCost: quoteResult.timeCost,
            additionalStopsCost: quoteResult.additionalStopsCost,
            lateNightSurcharge: quoteResult.lateNightSurcharge,
            gratuity: quoteResult.gratuity,
            pricingMethod: quoteResult.pricingMethod,
            flatRateDestination: quoteResult.flatRateDestination,
          },
        },
      });

      if (error) throw error;

      // Trigger success animation and sound
      setShowSuccessAnimation(true);
      playSuccessSound();
      setTimeout(() => setShowSuccessAnimation(false), 1500);

      toast({
        title: "Booking Request Sent!",
        description: "A team member will review this booking for availability.",
      });
      
      setShowBookingDialog(false);
      setCustomerName("");
      setCustomerEmail("");
      setCustomerPhone("");
    } catch (error) {
      console.error('Error sending quote email:', error);
      toast({
        title: "Error",
        description: "Failed to send quote. Please call us at 210-296-7409.",
        variant: "destructive",
      });
    } finally {
      setIsSendingEmail(false);
    }
  };

  return (
    <>
      <SuccessSparkles show={showSuccessAnimation} />
      <Card className="bg-[#1F2937] border-[#D4A574]/30 shadow-2xl">
      <CardContent className="p-6 md:p-8">
        {/* Service Type Toggle */}
        <div className="flex mb-8 bg-[#374151] rounded-lg p-1">
          <button
            onClick={() => setServiceType('airport')}
            className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-md transition-all font-semibold ${
              serviceType === 'airport'
                ? 'bg-[#3B82F6] text-white'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            <Plane className="w-4 h-4" />
            Airport Service
          </button>
          <button
            onClick={() => setServiceType('point-to-point')}
            className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-md transition-all font-semibold ${
              serviceType === 'point-to-point'
                ? 'bg-[#3B82F6] text-white'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            <Car className="w-4 h-4" />
            Point-to-Point
          </button>
        </div>

        {/* Input Fields */}
        <div className="space-y-6">
          {/* Pickup Address */}
          <div className="space-y-2">
            <Label htmlFor="pickup" className="text-[#D4A574] font-semibold uppercase text-xs tracking-wider flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              Pickup Address
            </Label>
            <Input
              id="pickup"
              type="text"
              placeholder={serviceType === 'airport' ? "e.g., SAT Airport, San Antonio, TX" : "Enter pickup address"}
              value={pickupAddress}
              onChange={(e) => setPickupAddress(e.target.value)}
              className="bg-[#374151] border-[#4B5563] text-white placeholder:text-gray-500 h-12"
            />
          </div>

          {/* Dropoff Address */}
          <div className="space-y-2">
            <Label htmlFor="dropoff" className="text-[#D4A574] font-semibold uppercase text-xs tracking-wider flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              Drop-off Address
            </Label>
            <Input
              id="dropoff"
              type="text"
              placeholder="Enter drop-off address"
              value={dropoffAddress}
              onChange={(e) => setDropoffAddress(e.target.value)}
              className="bg-[#374151] border-[#4B5563] text-white placeholder:text-gray-500 h-12"
            />
          </div>

          {/* Date and Time */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="date" className="text-[#D4A574] font-semibold uppercase text-xs tracking-wider flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Pickup Date
              </Label>
              <Input
                id="date"
                type="date"
                value={pickupDate}
                onChange={(e) => setPickupDate(e.target.value)}
                min={format(new Date(), 'yyyy-MM-dd')}
                className="bg-[#374151] border-[#4B5563] text-white h-12"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="time" className="text-[#D4A574] font-semibold uppercase text-xs tracking-wider flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Pickup Time
              </Label>
              <Input
                id="time"
                type="time"
                value={pickupTime}
                onChange={(e) => setPickupTime(e.target.value)}
                className="bg-[#374151] border-[#4B5563] text-white h-12"
              />
              {checkLateNight(pickupTime) && (
                <p className="text-xs text-amber-500 flex items-center gap-1">
                  <Info className="w-3 h-3" />
                  Late night surcharge (${FORMULA_PRICING.LATE_NIGHT_SURCHARGE}) applies
                </p>
              )}
            </div>
          </div>

          {/* Additional Stops */}
          <div className="space-y-2">
            <Label className="text-[#D4A574] font-semibold uppercase text-xs tracking-wider">
              Additional Stops (${FORMULA_PRICING.ADDITIONAL_STOP} each)
            </Label>
            <div className="flex items-center gap-4">
              <button
                onClick={() => setAdditionalStops(Math.max(0, additionalStops - 1))}
                disabled={additionalStops === 0}
                className="w-10 h-10 rounded-full bg-[#374151] border border-[#4B5563] text-white flex items-center justify-center hover:bg-[#4B5563] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Minus className="w-4 h-4" />
              </button>
              <span className="text-2xl font-bold text-white min-w-[3rem] text-center">
                {additionalStops}
              </span>
              <button
                onClick={() => setAdditionalStops(Math.min(5, additionalStops + 1))}
                disabled={additionalStops === 5}
                className="w-10 h-10 rounded-full bg-[#374151] border border-[#4B5563] text-white flex items-center justify-center hover:bg-[#4B5563] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Plus className="w-4 h-4" />
              </button>
              {additionalStops > 0 && (
                <span className="text-sm text-gray-400">
                  +${(additionalStops * 25).toFixed(2)}
                </span>
              )}
            </div>
          </div>

          {/* Optional Overrides */}
          <div className="border-t border-[#4B5563] pt-6">
            <p className="text-sm text-gray-400 mb-4">
              Optional: Enter distance/duration manually if Google Maps is unavailable
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="distance" className="text-gray-400 text-xs uppercase tracking-wider">
                  Distance (miles)
                </Label>
                <Input
                  id="distance"
                  type="number"
                  placeholder="e.g., 25"
                  value={distanceOverride}
                  onChange={(e) => setDistanceOverride(e.target.value)}
                  className="bg-[#374151] border-[#4B5563] text-white placeholder:text-gray-500 h-10"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="duration" className="text-gray-400 text-xs uppercase tracking-wider">
                  Duration (minutes)
                </Label>
                <Input
                  id="duration"
                  type="number"
                  placeholder="e.g., 35"
                  value={durationOverride}
                  onChange={(e) => setDurationOverride(e.target.value)}
                  className="bg-[#374151] border-[#4B5563] text-white placeholder:text-gray-500 h-10"
                />
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 pt-4">
            <Button
              onClick={calculateQuote}
              className="flex-1 bg-[#3B82F6] hover:bg-[#2563EB] text-white text-lg py-6 font-bold"
              disabled={isCalculating}
            >
              {isCalculating ? (
                <>
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                  Calculating...
                </>
              ) : (
                <>
                  <DollarSign className="w-5 h-5 mr-2" />
                  Get Instant Quote
                </>
              )}
            </Button>
            <Button
              onClick={resetForm}
              variant="outline"
              className="bg-transparent border-[#4B5563] text-gray-300 hover:bg-[#374151] hover:text-white py-6"
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              Reset Form
            </Button>
          </div>
        </div>

        {/* Results Display */}
        <AnimatePresence>
          {quoteResult && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.5 }}
              className="mt-8"
            >
              <div className="bg-gradient-to-br from-[#1a1a2e] to-[#16213e] border-2 border-[#D4A574]/40 rounded-xl p-6 md:p-8">
                {/* Detection Method Badge */}
                {detectionMethod && (
                  <div className="flex flex-col items-center gap-2 mb-4">
                    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${
                      detectionMethod === 'business' ? 'bg-amber-500/20 text-amber-400' :
                      detectionMethod === 'smart' ? 'bg-emerald-500/20 text-emerald-400' :
                      detectionMethod === 'google' ? 'bg-blue-500/20 text-blue-400' :
                      'bg-gray-500/20 text-gray-400'
                    }`}>
                      <Sparkles className="w-3 h-3" />
                      {detectionMethod === 'business' ? '📍 Business Recognized' :
                       detectionMethod === 'smart' ? 'Smart Detection' :
                       detectionMethod === 'google' ? 'Google Maps' :
                       'Manual Entry'}
                    </span>
                    {recognizedBusiness && (
                      <span className="text-xs text-amber-400/80">
                        {recognizedBusiness}
                      </span>
                    )}
                  </div>
                )}

                {/* Total Price */}
                <div className="text-center mb-6">
                  <p className="text-sm text-gray-400 uppercase tracking-wider mb-2">Your Estimated Total</p>
                  <div className="text-5xl md:text-6xl font-bold text-[#D4A574]">
                    ${quoteResult.totalPrice.toFixed(2)}
                  </div>
                  <p className="text-sm text-gray-400 mt-2">
                    {quoteResult.pricingMethod === 'flat-rate' 
                      ? `Flat Rate to ${quoteResult.flatRateDestination}` 
                      : quoteResult.pricingMethod === 'local' 
                        ? 'Local Service Rate' 
                        : 'Calculated Rate'}
                  </p>
                  {quoteResult.isFlat && (
                    <p className="text-xs text-emerald-400 mt-1">
                      ✓ Gratuity already included in flat rate
                    </p>
                  )}
                </div>

                {/* Price Breakdown */}
                <div className="space-y-3 mb-6 border-t border-[#4B5563] pt-6">
                  <h4 className="text-[#D4A574] font-semibold uppercase text-xs tracking-wider mb-4">
                    Price Breakdown
                  </h4>
                  
                  <div className="flex justify-between text-gray-300">
                    <span>
                      {quoteResult.pricingMethod === 'flat-rate' 
                        ? `Flat Rate (${quoteResult.flatRateDestination})` 
                        : quoteResult.pricingMethod === 'local' 
                          ? 'Local Base Fare' 
                          : 'Base Fare'}
                    </span>
                    <span className="font-semibold">${quoteResult.baseFare.toFixed(2)}</span>
                  </div>
                  
                  {quoteResult.mileageCost > 0 && (
                    <div className="flex justify-between text-gray-300">
                      <span>Mileage ({quoteResult.distance.toFixed(1)} mi × $3.25)</span>
                      <span className="font-semibold">${quoteResult.mileageCost.toFixed(2)}</span>
                    </div>
                  )}
                  
                  {quoteResult.timeCost > 0 && (
                    <div className="flex justify-between text-gray-300">
                      <span>Time ({Math.round(quoteResult.duration)} min × $1.25)</span>
                      <span className="font-semibold">${quoteResult.timeCost.toFixed(2)}</span>
                    </div>
                  )}
                  
                  {quoteResult.additionalStopsCost > 0 && (
                    <div className="flex justify-between text-gray-300">
                      <span>Additional Stops ({additionalStops} × $25)</span>
                      <span className="font-semibold">${quoteResult.additionalStopsCost.toFixed(2)}</span>
                    </div>
                  )}
                  
                  {quoteResult.lateNightSurcharge > 0 && (
                    <div className="flex justify-between text-amber-400">
                      <span>Late-Night/Early Morning Surcharge (9 PM - 5 AM)</span>
                      <span className="font-semibold">${quoteResult.lateNightSurcharge.toFixed(2)}</span>
                    </div>
                  )}
                  
                  {quoteResult.isFlat ? (
                    <div className="flex justify-between text-emerald-400 border-t border-[#4B5563] pt-3">
                      <span>Gratuity</span>
                      <span className="font-semibold">Included</span>
                    </div>
                  ) : (
                    <div className="flex justify-between text-[#D4A574] border-t border-[#4B5563] pt-3">
                      <span>Gratuity (20%)</span>
                      <span className="font-semibold">${quoteResult.gratuity.toFixed(2)}</span>
                    </div>
                  )}
                </div>

                {/* Trip Details */}
                <div className="grid grid-cols-2 gap-4 mb-6 border-t border-[#4B5563] pt-6">
                  <div className="text-center">
                    <p className="text-xs text-gray-400 uppercase tracking-wider">Distance</p>
                    <p className="text-xl font-bold text-white">{quoteResult.distanceText}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-gray-400 uppercase tracking-wider">Duration</p>
                    <p className="text-xl font-bold text-white">{quoteResult.durationText}</p>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="space-y-3">
                  {/* Book This Quote Button */}
                  <Button
                    onClick={() => setShowBookingDialog(true)}
                    className="w-full bg-[#D4A574] hover:bg-[#C49A6C] text-black font-semibold"
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    Book This Quote
                  </Button>
                  
                  {/* Share Quote Button - Opens Luxury Preview Card */}
                  <Button
                    onClick={() => setShowShareDialog(true)}
                    className="w-full bg-gradient-to-r from-yellow-700 via-yellow-500 to-yellow-700 hover:from-yellow-600 hover:via-yellow-400 hover:to-yellow-600 text-black font-semibold"
                  >
                    <Share2 className="w-4 h-4 mr-2" />
                    Share Quote
                  </Button>
                  
                  {/* Copy Button */}
                  <Button
                    onClick={copyToClipboard}
                    variant="outline"
                    className="w-full bg-transparent border-[#D4A574]/50 text-[#D4A574] hover:bg-[#D4A574]/10"
                  >
                    {copied ? (
                      <>
                        <Check className="w-4 h-4 mr-2" />
                        Copied to Clipboard!
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4 mr-2" />
                        Copy Quote Details
                      </>
                    )}
                  </Button>
                </div>

                {/* Disclaimer */}
                <p className="text-xs text-gray-500 text-center mt-4">
                  * Final price may vary based on actual route, traffic conditions, and additional requirements.
                  All prices include 20% gratuity.
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Booking Dialog */}
        <Dialog open={showBookingDialog} onOpenChange={setShowBookingDialog}>
          <DialogContent className="bg-[#1F2937] border-[#D4A574]/30 text-white">
            <DialogHeader>
              <DialogTitle className="text-[#D4A574]">Book This Quote</DialogTitle>
              <DialogDescription className="text-gray-400">
                Enter your contact information and we'll reach out to confirm your booking.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 mt-4">
              {quoteResult && (
                <div className="bg-[#374151] rounded-lg p-4 mb-4">
                  <p className="text-sm text-gray-400">Quote Total</p>
                  <p className="text-2xl font-bold text-[#D4A574]">${quoteResult.totalPrice.toFixed(2)}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    {pickupAddress} → {dropoffAddress}
                  </p>
                </div>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="customerName" className="text-[#D4A574] flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Full Name
                </Label>
                <Input
                  id="customerName"
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  placeholder="Your full name"
                  className="bg-[#374151] border-[#4B5563] text-white"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="customerEmail" className="text-[#D4A574] flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  Email
                  {/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customerEmail) && (
                    <motion.div
                      initial={{ scale: 0, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ type: "spring", stiffness: 500, damping: 15 }}
                    >
                      <Check className="w-4 h-4 text-emerald-400 drop-shadow-[0_0_4px_rgba(52,211,153,0.6)]" />
                    </motion.div>
                  )}
                </Label>
                <div className="relative">
                  <Input
                    id="customerEmail"
                    type="email"
                    value={customerEmail}
                    onChange={(e) => setCustomerEmail(e.target.value)}
                    placeholder="your@email.com"
                    className={`bg-[#374151] border-[#4B5563] text-white pr-10 ${
                      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customerEmail) 
                        ? 'border-emerald-500/50 focus:border-emerald-500' 
                        : customerEmail.length > 0 
                          ? 'border-amber-500/50' 
                          : ''
                    }`}
                  />
                  {customerEmail.length > 0 && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      {/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customerEmail) ? (
                        <motion.div
                          initial={{ scale: 0, rotate: -180 }}
                          animate={{ scale: 1, rotate: 0 }}
                          transition={{ type: "spring", stiffness: 400, damping: 12 }}
                        >
                          <Check className="w-4 h-4 text-emerald-400 drop-shadow-[0_0_6px_rgba(52,211,153,0.8)]" />
                        </motion.div>
                      ) : (
                        <span className="text-xs text-amber-400">
                          Invalid
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="customerPhone" className="text-[#D4A574] flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  Phone
                  {customerPhone.replace(/\D/g, '').length === 10 && (
                    <motion.div
                      initial={{ scale: 0, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ type: "spring", stiffness: 500, damping: 15 }}
                    >
                      <Check className="w-4 h-4 text-emerald-400 drop-shadow-[0_0_4px_rgba(52,211,153,0.6)]" />
                    </motion.div>
                  )}
                </Label>
                <div className="relative">
                  <Input
                    id="customerPhone"
                    type="tel"
                    value={customerPhone}
                    onChange={handlePhoneChange}
                    placeholder="(210) 555-1234"
                    className={`bg-[#374151] border-[#4B5563] text-white pr-10 ${
                      customerPhone.replace(/\D/g, '').length === 10 
                        ? 'border-emerald-500/50 focus:border-emerald-500' 
                        : customerPhone.length > 0 
                          ? 'border-amber-500/50' 
                          : ''
                    }`}
                    maxLength={14}
                  />
                  {customerPhone.length > 0 && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      {customerPhone.replace(/\D/g, '').length === 10 ? (
                        <motion.div
                          initial={{ scale: 0, rotate: -180 }}
                          animate={{ scale: 1, rotate: 0 }}
                          transition={{ type: "spring", stiffness: 400, damping: 12 }}
                        >
                          <Check className="w-4 h-4 text-emerald-400 drop-shadow-[0_0_6px_rgba(52,211,153,0.8)]" />
                        </motion.div>
                      ) : (
                        <span className="text-xs text-amber-400">
                          {customerPhone.replace(/\D/g, '').length}/10
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
              
              {/* Form validation check */}
              {(() => {
                const isNameValid = customerName.trim().length > 0;
                const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customerEmail);
                const isPhoneValid = customerPhone.replace(/\D/g, '').length === 10;
                const isFormValid = isNameValid && isEmailValid && isPhoneValid;
                
                return (
                  <motion.div
                    animate={isFormValid ? {
                      scale: [1, 1.02, 1],
                      transition: { duration: 0.3 }
                    } : {}}
                  >
                    <Button
                      onClick={sendQuoteEmail}
                      disabled={isSendingEmail}
                      className={`w-full font-semibold mt-4 transition-all duration-300 ${
                        isFormValid 
                          ? 'bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-500 hover:to-emerald-400 text-white shadow-lg shadow-emerald-500/30' 
                          : 'bg-[#D4A574] hover:bg-[#C49A6C] text-black'
                      }`}
                    >
                      {isSendingEmail ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                          Sending...
                        </>
                      ) : isFormValid ? (
                        <motion.span
                          className="flex items-center justify-center"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ duration: 0.2 }}
                        >
                          <Check className="w-4 h-4 mr-2" />
                          Ready to Submit
                        </motion.span>
                      ) : (
                        <>
                          <Mail className="w-4 h-4 mr-2" />
                          Send Quote & Request Booking
                        </>
                      )}
                    </Button>
                  </motion.div>
                );
              })()}
            </div>
          </DialogContent>
        </Dialog>

        {/* Share Quote Dialog with Luxury Preview Card */}
        <Dialog open={showShareDialog} onOpenChange={setShowShareDialog}>
          <DialogContent className="bg-zinc-950 border-yellow-600/30 text-white max-w-md max-h-[90vh] overflow-y-auto">
            {quoteResult && (
              <LuxuryQuotePreviewCard
                quote={{
                  pickupAddress,
                  dropoffAddress,
                  pickupDate,
                  pickupTime,
                  distance: quoteResult.distance,
                  duration: quoteResult.duration,
                  total: quoteResult.totalPrice,
                  additionalStops,
                  pricingMethod: quoteResult.pricingMethod,
                  flatRateDestination: quoteResult.flatRateDestination,
                  gratuityIncluded: quoteResult.isFlat,
                  baseFare: quoteResult.baseFare,
                  mileageCost: quoteResult.mileageCost,
                  timeCost: quoteResult.timeCost,
                  additionalStopsCost: quoteResult.additionalStopsCost,
                  lateNightSurcharge: quoteResult.lateNightSurcharge,
                  gratuity: quoteResult.gratuity,
                }}
                weather={weather}
                aiInsights={aiInsights}
                onClose={() => setShowShareDialog(false)}
              />
            )}
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
    </>
  );
};

export default FareCalculator;
